import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Avatar, STATUS_LABEL, STATUS_RING } from '@/components/ui/Avatar'
import { Spinner } from '@/components/ui/Spinner'
import { fetchFriends, refreshFriends, setFriendNickname } from '@/api/users'
import { useAuthStore } from '@/store/auth'
import { useSyncStore } from '@/store/sync'
import { toFriend } from '@/lib/steam'
import type { FriendResponse, FriendsListResponse } from '@/types'

interface FriendCardProps {
  p: FriendResponse
  onSaveNickname: (friendId: string, nickname: string | null) => void
}

function FriendCard({ p, onSaveNickname }: FriendCardProps) {
  const f = toFriend(p)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(f.nickname ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  function startEdit() {
    setValue(f.nickname ?? '')
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  function save() {
    setEditing(false)
    const trimmed = value.trim() || null
    if (trimmed !== (f.nickname ?? null)) {
      onSaveNickname(f.id, trimmed)
    }
  }

  function cancel() {
    setEditing(false)
    setValue(f.nickname ?? '')
  }

  return (
    <div className="cg-friend-card">
      <Avatar friend={f} size={48} />
      <div className="cg-friend-body">
        {editing ? (
          <>
            <input
              ref={inputRef}
              className="cg-friend-nickname-input"
              value={value}
              placeholder={f.name}
              onChange={(e) => setValue(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); save() }
                if (e.key === 'Escape') { e.preventDefault(); cancel() }
              }}
            />
            <div className="cg-friend-nickname-hint">Enter · Esc to cancel</div>
          </>
        ) : (
          <div
            className={f.nickname ? 'cg-friend-nickname' : 'cg-friend-name'}
            onClick={startEdit}
            title={f.nickname ? `Click to edit nickname (${f.name})` : 'Click to set nickname'}
            style={{ cursor: 'pointer' }}
          >
            {f.nickname ?? f.name}
          </div>
        )}
        {!editing && f.nickname && (
          <div className="cg-friend-realname">{f.name}</div>
        )}
        <div className="cg-friend-handle">@{f.handle}</div>
        <div className="cg-friend-status" data-status={f.status}>
          <span
            className="cg-legend-dot"
            style={{ background: STATUS_RING[f.status] }}
          />
          {STATUS_LABEL[f.status]}
        </div>
      </div>
    </div>
  )
}

export default function FriendsPage() {
  const user = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const isSyncing = useSyncStore((s) => s.isSyncing)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['friends'],
    queryFn: fetchFriends,
  })

  const { mutate: doRefresh, isPending: refreshing } = useMutation({
    mutationFn: refreshFriends,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['friends'] }),
  })

  const nicknameMutation = useMutation({
    mutationFn: ({ friendId, nickname }: { friendId: string; nickname: string | null }) =>
      setFriendNickname(friendId, nickname),
    onMutate: async ({ friendId, nickname }) => {
      await queryClient.cancelQueries({ queryKey: ['friends'] })
      const previous = queryClient.getQueryData<FriendsListResponse>(['friends'])
      queryClient.setQueryData<FriendsListResponse>(['friends'], (old) =>
        old
          ? { ...old, friends: old.friends.map((f) => f.steamId === friendId ? { ...f, nickname } : f) }
          : old
      )
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['friends'], ctx.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['friends'] }),
  })

  const isPrivate = user?.friendsProfilePrivate === true

  return (
    <div className="cg-screen">
      <div className="cg-page-header">
        <div>
          <h1>Friends &amp; Groups</h1>
          <p className="cg-page-sub">
            Friends with Common Ground accounts share the most data. Public-profile friends share
            what their Steam profile exposes. Private profiles are limited to games you can directly
            see.
          </p>
        </div>
        <button
          className="cg-btn"
          onClick={() => doRefresh()}
          disabled={refreshing || isLoading || isSyncing}
        >
          {refreshing ? <span className="cg-spin">↻</span> : '↻'} Refresh
        </button>
      </div>

      <div className="cg-legend">
        {(
          [
            {
              k: 'authed',
              label: 'Logged in to Common Ground',
              desc: 'Full library, wishlist, votes & reviews',
            },
            { k: 'public', label: 'Public Steam profile', desc: 'Library & playtime visible' },
            {
              k: 'limited',
              label: 'Private profile',
              desc: 'Limited info',
            },
          ] as const
        ).map((s) => (
          <div key={s.k} className="cg-legend-item">
            <span
              className="cg-legend-dot"
              style={{ background: STATUS_RING[s.k] }}
            />
            <div>
              <div className="cg-legend-label">{s.label}</div>
              <div className="cg-legend-desc">{s.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {isPrivate && (
        <div
          style={{
            background: 'var(--bg-1)',
            border: '1px solid var(--line-soft)',
            borderRadius: 'var(--radius-lg)',
            padding: '12px 16px',
            fontSize: '12.5px',
            color: 'var(--fg-2)',
          }}
        >
          Your Steam profile is set to private. Friends who sync from their end will still appear
          here, but you won't see friends whose profiles are also private.
        </div>
      )}

      <div className="cg-section-header">
        <h2>All friends</h2>
        {data && (() => {
          const authedCount = data.friends.filter((f) => f.status === 'authed').length
          return (
            <span className="cg-page-sub">
              {data.friends.length} Steam friends · {authedCount} on Common Ground
            </span>
          )
        })()}
      </div>

      {isLoading && <Spinner />}

      {isError && (
        <p style={{ color: 'var(--fg-3)', fontSize: '13px' }}>
          Could not load friends. Try refreshing.
        </p>
      )}

      {data && data.friends.length === 0 && !isLoading && (
        <p style={{ color: 'var(--fg-3)', fontSize: '13px' }}>
          No Steam friends found. Press the refresh button to sync your friends list from Steam.
        </p>
      )}

      {data && data.friends.length > 0 && (
        <div className="cg-friend-grid">
          {[...data.friends]
            .sort((a, b) => (a.nickname || a.name).localeCompare(b.nickname || b.name))
            .map((p) => (
              <FriendCard
                key={p.steamId}
                p={p}
                onSaveNickname={(friendId, nickname) =>
                  nicknameMutation.mutate({ friendId, nickname })
                }
              />
            ))}
        </div>
      )}
    </div>
  )
}
